import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import LanguageSelector from './LanguageSelector';
import { useTranslation } from '../hooks/useTranslation';
import { ArrowLeft, Settings } from 'lucide-react';
import { 
  LuxuryMenu as Menu, 
  LuxuryClose as X, 
  LuxurySearch as Search, 
  LuxuryHeart as Heart,
  LuxuryUser as User,
  LuxuryPhone as Phone,
  LuxuryMail as Mail,
  LuxuryCompass as Compass,
  LuxuryHome as Home,
  LuxurySparkles as Sparkles
} from './icons/LuxuryIcons';

const Navigation = ({ villaData = null, onBooking = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Vérifier si on est sur une page de détail de villa ou recherche
  const isVillaDetailPage = location.pathname.startsWith('/villa/');
  const isSearchPage = location.pathname === '/search';

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigationItems = [
    { name: t('nav.home'), path: '/', icon: Home },
    { name: t('nav.search'), path: '/search', icon: Search },
    { name: t('nav.inspirations'), path: '/inspirations', icon: Compass },
    { name: t('nav.about'), path: '/about', icon: Sparkles }
  ];

  // Ajouter Admin seulement si on n'est pas sur une page villa
  const displayItems = isVillaDetailPage 
    ? navigationItems 
    : [...navigationItems, { name: 'Admin', path: '/admin', icon: Settings }];

  const isActive = (path) => location.pathname === path;

  const handleNavigation = (path) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  // Dynamic styles based on scroll state
  const shouldShowWhiteBg = isScrolled || isSearchPage;
  const navClasses = `fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-in-out ${
    shouldShowWhiteBg
      ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-100' 
      : 'bg-transparent'
  }`;

  const textColor = shouldShowWhiteBg ? 'text-gray-800' : 'text-white';
  const hoverTextColor = shouldShowWhiteBg ? 'hover:text-amber-600' : 'hover:text-amber-300';
  const logoHoverColor = shouldShowWhiteBg ? 'hover:text-amber-600' : 'hover:text-amber-300';

  return (
    <nav className={navClasses}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo ou Bouton Retour */}
          {isVillaDetailPage ? (
            <Button
              variant="ghost"
              onClick={() => navigate('/search')}
              className={`flex items-center gap-2 font-light transition-colors duration-300 ${
                shouldShowWhiteBg
                  ? 'text-gray-600 hover:text-gray-900' 
                  : 'text-white hover:text-amber-200'
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              Retour aux villas
            </Button>
          ) : (
            <div 
              className="cursor-pointer"
              onClick={() => handleNavigation('/')}
            >
              <h1 
                className={`text-2xl font-light tracking-wide transition-colors duration-300 ${textColor} ${logoHoverColor}`}
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                PRIVIA
              </h1>
            </div>
          )}

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {displayItems.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavigation(item.path)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  isActive(item.path)
                    ? shouldShowWhiteBg
                      ? 'text-amber-600 bg-amber-50'
                      : 'text-amber-300 bg-white/10 backdrop-blur-sm'
                    : shouldShowWhiteBg
                      ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      : 'text-white/90 hover:text-white hover:bg-white/10 backdrop-blur-sm'
                }`}
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <item.icon className="w-4 h-4" />
                {item.name}
              </button>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-4">
            {isVillaDetailPage && villaData ? (
              <>
                <Badge 
                  variant="outline" 
                  className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border-amber-200 text-amber-800 font-light"
                >
                  <Home className="w-4 h-4" />
                  {villaData.propertyType || 'Villa'}
                </Badge>
                
                <Button
                  onClick={onBooking}
                  className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-6 py-2.5 rounded-full font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  Réserver Maintenant
                </Button>
              </>
            ) : (
              <>
                <LanguageSelector 
                  variant="ghost"
                  isScrolled={shouldShowWhiteBg}
                  className="mr-2"
                />

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/favorites')}
                  className={`transition-colors duration-300 ${
                    shouldShowWhiteBg
                      ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50' 
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <Heart className="w-4 h-4 mr-2" />
                  {t('nav.favorites')}
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/login')}
                  className={`transition-colors duration-300 ${
                    shouldShowWhiteBg
                      ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50' 
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <User className="w-4 h-4 mr-2" />
                  {t('nav.login')}
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`transition-colors duration-300 ${
                shouldShowWhiteBg ? 'text-gray-600' : 'text-white'
              }`}
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className={`md:hidden border-t py-4 transition-colors duration-300 ${
            shouldShowWhiteBg ? 'border-gray-100' : 'border-white/20'
          }`}>
            <div className="flex flex-col space-y-3">
              {displayItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item.path)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all duration-300 ${
                    isActive(item.path)
                      ? shouldShowWhiteBg
                        ? 'text-amber-600 bg-amber-50'
                        : 'text-amber-300 bg-white/10 backdrop-blur-sm'
                      : shouldShowWhiteBg
                        ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        : 'text-white/90 hover:text-white hover:bg-white/10 backdrop-blur-sm'
                  }`}
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <item.icon className="w-4 h-4" />
                  {item.name}
                </button>
              ))}
              
              <div className={`border-t pt-3 mt-3 transition-colors duration-300 ${
                shouldShowWhiteBg ? 'border-gray-100' : 'border-white/20'
              }`}>
                <LanguageSelector 
                  variant="ghost"
                  isScrolled={shouldShowWhiteBg}
                  className="w-full justify-start mb-2"
                />

                <Button
                  variant="ghost"
                  size="sm"
                  className={`w-full justify-start mb-2 transition-colors duration-300 ${
                    isScrolled 
                      ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50' 
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <Heart className="w-4 h-4 mr-3" />
                  {t('nav.favorites')}
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  className={`w-full justify-start mb-3 transition-colors duration-300 ${
                    isScrolled 
                      ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50' 
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <User className="w-4 h-4 mr-3" />
                  {t('nav.login')}
                </Button>

                <Button
                  className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white py-2 rounded-full font-medium"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  {t('nav.speakToAdvisor')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;